import { VideoProviderType } from "front-end/components/video-box/types/video-provider.type";
import { youtubeVideoPlayerFactory } from "front-end/components/video-box/factories/youtube/youtube-video-player.factory";
import { EmptyInstance } from "core/features/components/mixins/empty-instance";
import { IVideoPlayerComponent } from "front-end/components/video-box/factories/video-player.component";
import { EventEmitterMixin } from "front-end/mixins/event-emitter.mixin";
import { IVideoPlayerFactory } from "front-end/components/video-box/factories/video-player.factory";
import { vimeoVideoPlayerFactory } from "front-end/components/video-box/factories/vimeo/vimeo-video-player.factory";
import { umbracoVideoPlayerFactory } from "front-end/components/video-box/factories/umbraco/umbraco-video-player.factory";
import { Subscription } from "rxjs";

export class VideoBoxComponent extends EventEmitterMixin(EmptyInstance) {
    public static SELECTOR = ".js-video-box";

    private _currentContext: JQuery = $();

    private _classMap = {
        css: {
            currentContext: {
                show: "show",
                hide: "hide"
            },
            playButton: {
                play: "play",
                pause: "pause"
            }
        },
        js: {
            playButton: `${VideoBoxComponent.SELECTOR}__play-button`
        }
    };

    private _playButton: JQuery = $();

    private _videoPlayerFactory: IVideoPlayerFactory = null;
    private _videoPlayer: IVideoPlayerComponent = null;

    private _onPlayingStateChanged: Subscription = null;
    private _playerCreated: Subscription = null;

    private get videoFrameId() : string {
        return this._currentContext.attr("data-video-frame-id");
    }

    private get providerType() : VideoProviderType {
        return this._currentContext.data("provider") as VideoProviderType;
    }

    private resolveVideoProvider(): IVideoPlayerFactory {
        switch (this.providerType) {
            case "youtube":
                return youtubeVideoPlayerFactory;
            case "vimeo":
                return vimeoVideoPlayerFactory;
            case "umbraco":
                return umbracoVideoPlayerFactory;
        }
    }

    public playVideo(): void {
        this._videoPlayer.play();
    }

    public pauseVideo(): void {
        this._videoPlayer.pause();
    }

    private handlePlayButtonClick(): void {
        if(this._videoPlayer.isPlaying) {
            this._videoPlayer.pause();
        } else {
            this._videoPlayer.play();
        }
    }

    private handlePlayingState(isPlaying: boolean): void {
        if(isPlaying) {
            this._playButton
                .addClass(this._classMap.css.playButton.pause)
                .removeClass(this._classMap.css.playButton.play);
        } else {
            this._playButton
                .addClass(this._classMap.css.playButton.play)
                .removeClass(this._classMap.css.playButton.pause);
        }
    }

    private bindEvents(): void {
        this._playButton
            .on("click", () => this.handlePlayButtonClick());

        this._onPlayingStateChanged = this._videoPlayer.playingState()
            .subscribe(isPlaying => this.handlePlayingState(isPlaying));
    }

    private parseContext(): void {
        this._playButton = $(this._classMap.js.playButton, this._currentContext);
    }

    public show(): void {
        this._currentContext
            .addClass(this._classMap.css.currentContext.show)
            .removeClass(this._classMap.css.currentContext.hide);
        this.playVideo();
    }

    public hide(): void {
        this._currentContext
            .addClass(this._classMap.css.currentContext.hide)
            .removeClass(this._classMap.css.currentContext.show);
        this.pauseVideo();
    }

    public destroy(): void {
        if (this._videoPlayer) {
            this._videoPlayer.destroy();
        }

        if (this._onPlayingStateChanged) {
            this._onPlayingStateChanged.unsubscribe();
        }

        if (this._playerCreated) {
            this._playerCreated.unsubscribe();
        }
    }

    public init(context: JQuery): void {
        this._currentContext = context || this._currentContext;
        this.parseContext();

        this._videoPlayerFactory = this.resolveVideoProvider();

        this._playerCreated = this._videoPlayerFactory.createPlayer(this.videoFrameId)
            .subscribe(player => {
                this._videoPlayer = player;
                this._videoPlayer.mute();
                this._videoPlayer.loop(true);
                this.bindEvents();
                this.emit("ready");
            });
    }
}
